import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { usePuck } from "@measured/puck";
import { useQuery } from "@tanstack/react-query";
import { fetchCatalogList } from "@/api/cms-puck/catalog-picker-api";
import { useCatalogFlowPreview } from "@/context/cms-puck/CatalogFlowPreviewContext";
import type { SelectionItem } from "@/lib/cms-puck/blocks/selection";

export type CatalogItemPreviewValue = {
  itemId: string | null;
  itemType: "artwork" | "multipleprint" | null;
  isLoading: boolean;
};

const CatalogItemPreviewContext = createContext<CatalogItemPreviewValue>({
  itemId: null,
  itemType: null,
  isLoading: false,
});

function readSelectionItem(
  raw: unknown,
): SelectionItem | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as { type?: string; id?: string };
  if (o.type === "artwork" && o.id) return { type: "artwork", id: o.id };
  if (o.type === "multipleprint" && o.id)
    return { type: "multipleprint", id: o.id };
  return null;
}

function findCatalogItemPreviewSelection(data: unknown): SelectionItem | null {
  if (!data || typeof data !== "object") return null;
  const content = (data as { content?: unknown }).content;
  if (!Array.isArray(content)) return null;

  const walk = (nodes: unknown[]): SelectionItem | null => {
    for (const node of nodes) {
      if (!node || typeof node !== "object") continue;
      const n = node as {
        type?: string;
        props?: Record<string, unknown>;
      };
      if (n.type === "CatalogueItemDetail") {
        const item = readSelectionItem(n.props?.item);
        if (item) return item;
      }
      if (n.props) {
        for (const v of Object.values(n.props)) {
          if (
            Array.isArray(v) &&
            v.every(
              (x) =>
                x != null &&
                typeof x === "object" &&
                "type" in (x as object),
            )
          ) {
            const found = walk(v);
            if (found) return found;
          }
        }
      }
    }
    return null;
  };

  return walk(content);
}

export function CatalogItemPreviewProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { appState } = usePuck();
  const puckData = appState.data;
  const { target, isLoading: flowLoading } = useCatalogFlowPreview();

  const stored = findCatalogItemPreviewSelection(puckData);
  const listType: "artwork" | "multipleprint" =
    stored?.type === "multipleprint"
      ? "multipleprint"
      : stored?.type === "artwork"
        ? "artwork"
        : target === "MULTIPRINTS"
          ? "multipleprint"
          : "artwork";

  const listQuery = useQuery({
    queryKey: ["catalog-item-preview-default", listType],
    queryFn: () => fetchCatalogList(listType, ""),
    enabled: !stored?.id,
    staleTime: 30_000,
  });

  const defaultId = listQuery.data?.[0]?.id ?? null;

  const itemId = stored?.id ?? defaultId;
  const itemType: "artwork" | "multipleprint" | null = itemId
    ? listType
    : null;

  const value = useMemo(
    (): CatalogItemPreviewValue => ({
      itemId,
      itemType,
      isLoading: flowLoading || listQuery.isLoading,
    }),
    [flowLoading, itemId, itemType, listQuery.isLoading],
  );

  return (
    <CatalogItemPreviewContext.Provider value={value}>
      {children}
    </CatalogItemPreviewContext.Provider>
  );
}

export function useCatalogItemPreview(): CatalogItemPreviewValue {
  return useContext(CatalogItemPreviewContext);
}
